'use client'
import React, { useState } from 'react';
import { SocialShareUrlButtonProps, ShareButtonProps } from '@interfaces';
import { HandleShareUrlClick } from './../../functions';
import styles from './../../styles/Social.module.css';

const PLATFORM_LABELS: Record<string, string> = {
  x: 'X',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  reddit: 'Reddit',
  pinterest: 'Pinterest',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  threads: 'Threads',
  bluesky: 'Bluesky',
  minds: 'Minds',
  tumblr: 'Tumblr',
  mailto: 'Email',
};


// Default set covers every platform with a configured share intent.
const DEFAULT_PLATFORMS = [
  'x', 'facebook', 'linkedin', 'reddit','threads','minds', 'mailto',
];

export const ShareButton: React.FC<ShareButtonProps> = ({
  platform = 'x',
  text = '',
  url = '',
  via = '',
  hashtags = '',
  title = '',
  description = '',
  summary = '',
  image = '',
}) => {
  // Many platforms ship a branded icon under /imgs/social/{p}Share/. If one is
  // missing we fall back to a labelled chip instead of a broken image.
  const [imgError, setImgError] = useState(false);
  const label = PLATFORM_LABELS[platform] || platform;

  // Richer fields resolve by alias in the share builder (Reddit title,
  // Pinterest media/description, …); unused fields are ignored per platform.
  const extras: Record<string, string | null> = {
    title: title || null,
    description: description || null,
    summary: summary || null,
    media: image || null,
  };

  return (
    <a
      href="#"
      target="_blank"
      rel="noopener noreferrer"
      title={`Share on ${label}`}
      aria-label={`Share on ${label}`}
      className={styles.shareItem}
      onClick={(e) => HandleShareUrlClick(e, platform, text, url, via, hashtags, extras)}
    >
      {imgError ? (
        <span className={styles.shareChip}>{label}</span>
      ) : (
        <img
          src={`/imgs/social/${platform}Share/${platform}Share.jpg`}
          alt={`Share on ${label}`}
          className={styles.socialShareImg}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      )}
    </a>
  );
};

// Copy-the-page-link control. Uses the async clipboard API where available and
// falls back to a hidden textarea + execCommand on older/insecure contexts.
export const CopyLinkButton: React.FC<{ url?: string }> = ({ url = '' }) => {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    const link = url || (typeof window !== 'undefined' ? window.location.href : '');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked — no-op */ }
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      title="Copy link"
      aria-label="Copy link to this page"
      className={styles.shareItem}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
    >
      <span className={styles.shareChip}>{copied ? 'Copied!' : '🔗 Copy link'}</span>
    </button>
  );
};

export const SocialShareButtons: React.FC<SocialShareUrlButtonProps> = (props) => {
  const platforms = props.platforms?.length ? props.platforms : DEFAULT_PLATFORMS;

  return (
    <div className={styles.shareBar}>
      <CopyLinkButton url={props.url} />
      {platforms.map((platform) => (
        <ShareButton
          key={platform}
          platform={platform}
          text={props.text}
          url={props.url}
          via={props.via}
          hashtags={props.hashtags}
          title={props.title}
          description={props.description}
          summary={props.summary}
          image={props.image}
        />
      ))}
    </div>
  );
};

export default SocialShareButtons;
